import type { OrganizationMemberResponse } from 'generatedTypes/user/models/organizationMemberResponse';
import { useState } from 'react';

import useFetch from 'hooks/useFetch';
import useRetry from 'hooks/useRetry';

import useTranslation from './hooks/useTranslation';

import { INVITATIONS_API, ROLES_PATH, USERS_API } from './constants';
import type {
    AssignUserRoles,
    FetchUser,
    InviteUser,
    RemoveUserRoles,
    UpdateUser,
    UserResponse
} from './types';

const useUser = () => {
    const [isLoading, setIsLoading] = useState(false);

    const { authenticatedDelete, authenticatedGet, authenticatedPost, authenticatedPut } =
        useFetch();
    const { withRetry } = useRetry();
    const { CONNECTION_ERROR, INVITE_FAILED, SAVE_FAILED } = useTranslation();

    const fetchUser: FetchUser = async ({ id }) => {
        const uri = `${USERS_API}/${id}`;

        try {
            setIsLoading(true);

            return await withRetry(
                () => authenticatedGet<OrganizationMemberResponse>(uri),
                CONNECTION_ERROR,
                () => fetchUser({ id })
            );
        } finally {
            setIsLoading(false);
        }
    };

    const updateUser: UpdateUser = async ({ id, user }) => {
        const uri = `${USERS_API}/${id}`;

        try {
            setIsLoading(true);

            return await withRetry(
                () => authenticatedPut<UserResponse>(uri, JSON.stringify(user)),
                SAVE_FAILED,
                () => updateUser({ id, user })
            );
        } finally {
            setIsLoading(false);
        }
    };

    const inviteUser: InviteUser = async ({ user }) => {
        const uri = `${INVITATIONS_API}`;

        try {
            setIsLoading(true);
            const result = await withRetry(
                () => authenticatedPost(uri, JSON.stringify(user)),
                INVITE_FAILED,
                () => inviteUser({ user })
            );

            return result ?? null;
        } finally {
            setIsLoading(false);
        }
    };

    const assignUserRoles: AssignUserRoles = async ({ id, roleAssignment }) => {
        const uri = `${USERS_API}/${id}/${ROLES_PATH}`;

        try {
            setIsLoading(true);

            const result = await withRetry(
                async () => {
                    const { error } = await authenticatedPost(uri, JSON.stringify(roleAssignment));

                    return { data: error ? null : true, error };
                },
                SAVE_FAILED,
                () => assignUserRoles({ id, roleAssignment })
            );

            return result !== null;
        } finally {
            setIsLoading(false);
        }
    };

    const removeUserRoles: RemoveUserRoles = async ({ id, roleAssignment }) => {
        const uri = `${USERS_API}/${id}/${ROLES_PATH}`;

        try {
            setIsLoading(true);

            const result = await withRetry(
                async () => {
                    const { error } = await authenticatedDelete(
                        uri,
                        JSON.stringify(roleAssignment)
                    );

                    return { data: error ? null : true, error };
                },
                SAVE_FAILED,
                () => removeUserRoles({ id, roleAssignment })
            );

            return result !== null;
        } finally {
            setIsLoading(false);
        }
    };

    return {
        assignUserRoles,
        fetchUser,
        inviteUser,
        isLoading,
        removeUserRoles,
        updateUser
    };
};

export default useUser;
